/*
 * WHOSE WORDS THESE ARE, AND ON WHAT TERMS.
 *
 * Every text names the translation it is read in and the licence that translation carries; and the
 * publishing gate discloses what is NOT here — the editions held back, on what licence, how many
 * passages, and what each one waits on. That last part is the point: a reader who knows the Talmud
 * exists is told plainly it is a permissions question, not an editorial one. The old app carried this
 * as its Sources panel; this restores it. Shares the browse-panel chrome with the other overlays.
 */
import type { Corpus, ReportSource } from '@versesverse/schema/types';
import type { AbsentBranch, Blocker } from '@versesverse/schema/absent';
import type { UnshelvedText } from '@versesverse/schema/unshelved';
import { supplierName } from '@versesverse/schema/suppliers';
import { useLang } from '../i18n/lang';

export interface SourceText {
  longName: string;
  /**
   * The published English edition, or null where there is none.
   *
   * Every scripture here has one and names a translator. The hadith do not: no openly-licensed
   * complete English of Bukhari or Muslim exists and none can be licensed, so a reader who does
   * not read Arabic gets an on-device machine reading and nothing else. Null renders as that
   * fact rather than as a blank where a name should be — the reader is owed the difference
   * between "translated by Pickthall" and "translated by nobody".
   */
  translation: string | null;
  licence: string;
  /** Whose copy this is — resolved to a name for the reader, never the slug. */
  supplier?: string;
  originalEdition: string;
  originalLanguage: string;
  secondTranslation?: string;
  /**
   * A third English, which only the Hebrew Bible has — and it is the one a reader most needs named.
   *
   * The King James and the Berean Standard Bible are both Christian translations, and this text is
   * read by two traditions. The JPS Tanakh of 1917 is the Jewish Publication Society's rendering of
   * the Masoretic text, carried on 23,128 verses precisely so that the Jewish reading is present —
   * where it differs, it differs about what the two traditions have argued over, and Isaiah 7:14 is
   * "the young woman" here against "a virgin" in the other two.
   *
   * It reached the page and not the sources panel, so the panel listed two Christian Englishes and
   * stopped. A text carried for representation that is not named where sources are named is doing
   * only half its work.
   */
  thirdTranslation?: string;
  /**
   * The fourth English and beyond, each with its own licence.
   *
   * The three fields above stop where somebody stopped adding fields. These are the rest, and
   * they belong in this panel for the same reason the first three do: a reader deciding whether
   * to trust a rendering is asking whose it is and on what terms. Each carries its OWN licence
   * because licences belong to translations rather than to shelves — the Douay-Rheims is public
   * domain by age while the corpus around it may not be, and a panel showing one licence for a
   * text offering four Englishes would be stating something false about three of them.
   */
  /*
   * TAKEN FROM THE SCHEMA RATHER THAN RESTATED — 2026-08-17.
   *
   * This was written out by hand as `{ id, name, licence?, note? }` and had already fallen two
   * fields behind: `lang` and `rtl` were added to the registry when the renderings stopped being
   * English, and `verses` today. Nothing broke, because the panel happens not to read the first
   * two — a copied type goes stale silently and only fails when somebody finally needs the field.
   *
   * One definition, in the workspace that owns it.
   */
  extraTranslations?: Corpus['extraTranslations'];
  /**
   * A condition that outlives the fetch, or an editorial stance a reader is owed.
   *
   * `licence` says what we were permitted. This says what the reader needs in order to read
   * the text as what it is — QuranEnc publishes under the methodology of Ahl-us-Sunnah
   * wal-Jamaa`ah, which is the majority Sunni tradition and not a neutral one. A library
   * whose whole purpose is showing every side must not let that arrive unannounced.
   */
  translationNote?: string;
  /* The same, for the ORIGINAL. A fact about the source text read as a fact about the English is
     worse than no fact, so it is a separate line rather than appended to the note above. */
  originalNote?: string;
  /**
   * Who holds this text as scripture, and who does not — for a text a tradition is divided about.
   *
   * The hadith are the case that forced it onto the page. Bukhari and Muslim are the Sunni
   * Sahihayn; Shia Islam follows the Four Books transmitted through the Ahl al-Bayt and does
   * not accept many of these narrators. Filing them under "Islam" and saying nothing tells a
   * Shia reader this is their tradition.
   *
   * Both halves are required by `schema/validate.ts` and both are printed, because naming only
   * who holds a text is the vagueness the field exists to replace.
   */
  partialCanon?: { tradition: string; heldBy: string; notHeldBy: string };
  /**
   * A tradition that holds MORE than this text carries — not one that rejects it.
   *
   * The Hebrew Bible ships the 39-book canon and is offered to Christianity as "Old
   * Testament". Catholic Christians hold 46 books and the Orthodox churches more, so a
   * Catholic reader was shown a canon labelled as theirs with nothing saying what was absent.
   *
   * Kept apart from `partialCanon` because the two make different claims and only one is true
   * here: `notHeldBy` would say Catholics do not count the Hebrew Bible as scripture, and they
   * do. Hence "Also held as scripture by".
   */
  longerCanon?: { tradition: string; heldBy: string; beyond: string };
  /**
   * A tradition that holds FEWER books than this text carries — the third direction.
   *
   * The New Testament ships 27 books. The Assyrian Church of the East reads the Peshitta, whose
   * canon is 22: 2 Peter, 2 and 3 John, Jude and Revelation were never in it. `partialCanon`
   * would say they do not hold the New Testament as scripture, which is false — it is the only
   * canonical text of their church. `longerCanon` is the opposite fact.
   *
   * So this corpus disclosed the Ethiopian canon of 35 and said nothing about the canon of 22,
   * because the structure had a word for one and not the other. Hence "Not in the canon of".
   */
  shorterCanon?: { tradition: string; heldBy: string; without: string };
  /** Who keyed the original, where somebody asserts something over having done it. */
  digitisation?: string;
  /**
   * The collections a report source draws on, and how many passages each contributes.
   *
   * The hadith arrive under one heading — "Sahih al-Bukhari · Sahih Muslim" — which names both
   * and quantifies neither. `collections` was in the bundle, was read by `qa-artifacts` to check
   * the report files against it, and reached no reader at all: the fourth field in one night
   * that was declared, correct, and never forwarded.
   *
   * It is worth showing rather than declaring internal. These are two separate collections with
   * separate compilers, and a reader asking which source a report belongs to is asking exactly
   * what this answers.
   */
  collections?: { name: string; passages: number; standing?: string }[];
}

/*
 * WHICH CORPUS FIELDS ARE NOT THE READER'S BUSINESS.
 *
 * Everything a `Corpus` declares is either shown in this panel or named here as internal. There
 * is no third option, and a test enforces that against the real registry — so a field added to
 * `Corpus` fails the suite until somebody has decided which it is.
 *
 * WHY THAT ENFORCEMENT EXISTS. Three separate defects in one night were the same shape: a field
 * that was declared, populated, correct, and never forwarded to the reader. `prov` shipped empty
 * through five layers. `longerCanon` was validated in two places and declared on nothing. And
 * `thirdTranslation` — the JPS Tanakh, carried on 23,128 verses precisely so the Jewish reading
 * is present — reached the page but not the panel where sources are named, leaving a text read
 * by two traditions listing two Christian Englishes and no Jewish one.
 *
 * Each was found by a person noticing. That is not a mechanism, and the data being right is
 * exactly what makes this class of bug quiet: every check passes, because nothing is wrong with
 * the data — the last hop is simply missing.
 */
const INTERNAL_CORPUS_FIELDS: readonly string[] = [
  'id',            // addressing
  'code',          // the single letter in a verse id
  'name',          // short name; `longName` is what a reader is shown
  'colour',        // map drawing
  'petal',         // map drawing: which sector of the wheel this text's names cluster in
  'traditions',    // drives the filter chips, shown there rather than here
  'spellingScopes',// which name-spelling table the pipeline searched with
  'readOnline',    // which external site the "read on" link points at
  'passageUnit',   // what bounds "the passage around this verse"
  'nonCommercial', // surfaced on the passage itself, beside the text it binds
];

/**
 * A corpus as the sources panel shows it.
 *
 * Extracted from App.tsx so it can be tested. It was inline there, which is how
 * `thirdTranslation` came to be missing: the mapping was nine lines in a JSX prop, and nothing
 * could look at it.
 */
export function sourceTextFor(c: Corpus): SourceText {
  return {
    longName: c.longName,
    translation: c.translation,
    licence: c.licence,
    supplier: c.supplier,
    originalEdition: c.original?.edition ?? '',
    originalLanguage: c.original?.language ?? '',
    secondTranslation: c.secondTranslation,
    thirdTranslation: c.thirdTranslation,
    extraTranslations: c.extraTranslations,
    translationNote: c.translationNote,
    originalNote: c.originalNote,
    partialCanon: c.partialCanon,
    longerCanon: c.longerCanon,
    shorterCanon: c.shorterCanon,
  };
}

/** The corpus fields this panel deliberately does not show, for the test that proves the rest are. */
export const INTERNAL_FIELDS = INTERNAL_CORPUS_FIELDS;

/**
 * How many passages the unshelved section is describing, added up here rather than written down.
 *
 * The number is a claim on a page — "33,125 passages are held and shown to nobody" — and a total
 * typed beside a list is wrong the first time the list changes. `pipeline/qa-staged.ts` checks the
 * same sum against what the files actually hold, so the page cannot claim more than exists.
 */
export function unshelvedTotal(items: readonly { passages: number }[]): number {
  return items.reduce((n, u) => n + u.passages, 0);
}

/**
 * Collections gathered by what the tradition says about them, in first-appearance order.
 *
 * Order matters and is taken from the data rather than sorted: `hadith-source.ts` lists the
 * Sahihayn first because Sunni tradition ranks them first, and re-sorting here would quietly
 * restate that ranking. Collections with no `standing` group together under the empty key and
 * render exactly as they did before this field existed.
 */
export function groupByStanding(
  collections: { name: string; passages: number; standing?: string }[] | undefined,
): [string, { name: string; passages: number; standing?: string }[]][] {
  const groups = new Map<string, { name: string; passages: number; standing?: string }[]>();
  for (const c of collections ?? []) {
    const key = c.standing ?? '';
    const into = groups.get(key) ?? [];
    into.push(c);
    groups.set(key, into);
  }
  return [...groups];
}

/**
 * The hadith source as the sources panel shows it.
 *
 * Extracted for the same reason `sourceTextFor` was, and it found the same bug: the mapping was
 * inline in App.tsx and `collections` — which names Bukhari and Muslim separately and counts what
 * each contributes — was never forwarded. `ReportSource` declares eight fields and seven arrived.
 */
export function sourceTextForReports(r: ReportSource): SourceText {
  return {
    longName: r.longName,
    translation: r.translation,
    licence: r.licence,
    originalEdition: r.originalEdition,
    originalLanguage: r.originalLanguage,
    digitisation: r.digitisation,
    /* The report source has no corpus entry, so its note arrives on its own field — rendered
       through the same slot a corpus uses for a stance a reader is owed. */
    translationNote: r.relation,
    collections: r.collections,
    /* Whose reports these are. The corpus slot renders it; these shelves had nowhere to put it
       until 2026-08-17, so 112,613 reports shipped without the sentence 2,328 of them carried. */
    partialCanon: r.partialCanon,
  };
}

export interface HeldBack {
  edition: string;
  licence: string;
  awaiting?: string;
  passages: number;
  /**
   * Which texts this held-back edition would have served, by the names a reader knows them by.
   *
   * The bundle carries corpus IDs — `["TALMUD"]` — because the publishing gate derives them from
   * the verses that use the edition. Those are addressing, not English, so they are resolved to
   * long names before they reach the page.
   *
   * The fifth field in one night that was declared, correct, and not forwarded. It matters here
   * more than the count does: "William Davidson Edition" tells a reader who already knows what
   * the Davidson edition is, and "the Talmud is missing because of it" tells everyone else.
   */
  corporaNames?: string[];
}

/** A held-back grant as the panel shows it, with corpus ids resolved to the names a reader sees. */
export function heldBackFor(
  grant: { edition: string; licence: string; awaiting?: string; passages: number; corpora: string[] },
  longNameById: (id: string) => string | undefined,
): HeldBack {
  return {
    edition: grant.edition,
    licence: grant.licence,
    awaiting: grant.awaiting,
    passages: grant.passages,
    /* An id with no corpus behind it is dropped rather than shown raw: a reader seeing "TALMUD"
       learns nothing they could not read in the edition name, and it looks like a bug. */
    corporaNames: grant.corpora.map(longNameById).filter((n): n is string => Boolean(n)),
  };
}

/*
 * WHY THIS IS A RECORD AND NOT A TERNARY.
 *
 * It was a ternary — `blockedBy === 'permission' ? … : …` — for as long as there were two
 * blockers. Adding a third did not break it; it silently labelled the new one "no publishable
 * original", which is a sentence about the world when the truth was a sentence about us.
 *
 * Typed by `Blocker`, so a value added to that union fails the build here until somebody has
 * decided what a reader should be told. That is the same rule `INTERNAL_CORPUS_FIELDS` enforces
 * for corpus fields, and for the same reason: the failure mode is not an error, it is a plausible
 * wrong label that nobody goes looking for.
 */
const BLOCKER_LABEL: Record<Blocker, 'sources.blockedPermission' | 'sources.blockedOriginal' | 'sources.blockedAddressing' | 'sources.blockedTranslation' | 'sources.blockedTranscription'> = {
  permission: 'sources.blockedPermission',
  original: 'sources.blockedOriginal',
  addressing: 'sources.blockedAddressing',
  /* Added with the Ibadi row, and the build refused to proceed without it — which is this
     Record's entire purpose working as described above, one blocker later. */
  translation: 'sources.blockedTranslation',
  /* And again with the Ibadi row, which moved OFF `translation` and then off `permission` too.
     The label matters more than the others: this is the one blocker a reader could remove
     themselves, so "awaiting permission" told them to wait when they could have helped. */
  transcription: 'sources.blockedTranscription',
};

/** A text whose term runs out on a known day, as the bundle carries it. */
export interface Expiry {
  text: string;
  /** ISO, always a 1 January — terms run to the end of a year. */
  freeFrom: string;
  unblocks: string;
}

/**
 * How far off a date is, in words a reader can act on.
 *
 * `lib/expiries.ts` learnt this the hard way and the lesson is repeated here rather than imported,
 * because the pipeline's copy is not reachable from the browser: it subtracted YEAR NUMBERS, so
 * 1 January 2027 read as "about a year" from any day in 2026 — including the week before. The error
 * runs in the one direction that matters, making a near date sound far.
 */
export function yearsAway(today: string, freeFrom: string): number {
  const from = new Date(`${today}T00:00:00Z`);
  const to = new Date(`${freeFrom}T00:00:00Z`);
  return (to.getUTCFullYear() - from.getUTCFullYear()) * 12
    + (to.getUTCMonth() - from.getUTCMonth())
    - (to.getUTCDate() < from.getUTCDate() ? 1 : 0);
}

export interface SourcesPanelProps {
  texts: SourceText[];
  heldBack: HeldBack[];
  /**
   * Branches the library carries nothing of.
   *
   * Kept apart from `heldBack` because the two are different admissions. A held-back edition is
   * ours — fetched, sitting in the corpus, withheld pending a grant. These were never obtained
   * at all, and telling a reader "awaiting permission" about a text this project does not have
   * would be a more comfortable sentence than the true one.
   */
  absent: AbsentBranch[];
  /**
   * Texts held, licensed, and waiting on a decision about where they belong.
   *
   * Kept apart from `heldBack` and `absent` because it is the only one of the three where the
   * hold-up is ours. Defaulted to empty so a caller that has not been updated shows nothing
   * rather than crashing — but `App.tsx` passes it, and the schema list is the source.
   */
  unshelved?: UnshelvedText[];
  /**
   * Texts that are missing only until a date.
   *
   * The fourth kind of absence, and the only hopeful one. `heldBack` waits on somebody's decision,
   * `absent` on a text nobody has, `unshelved` on ours — these wait on a calendar, and nothing can
   * stop them arriving. A reader told only that a text is missing is being given the pessimistic
   * half of a true story.
   *
   * Optional and defaulted for the same reason `unshelved` is: a bundle built before the field
   * existed shows nothing rather than crashing.
   */
  expiries?: Expiry[];
  onClose: () => void;
}

export function SourcesPanel({ texts, heldBack, absent, unshelved = [], expiries = [], onClose }: SourcesPanelProps): React.JSX.Element {
  const { t } = useLang();
  return (
    <aside className="tellings" role="region" aria-label={t('sources.open')}>
      <div className="tellings-head">
        <h2 className="tellings-title">{t('sources.open')}</h2>
        <button type="button" className="tellings-close" onClick={onClose} aria-label={t('action.close')}>×</button>
      </div>
      <div className="tellings-body">
        <p className="tellings-lead">{t('sources.lead')}</p>

        <ul className="sources-list">
          {texts.map(text => (
            <li key={text.longName} className="source-item">
              <h3 className="source-name">{text.longName}</h3>
              {text.translation === null ? (
                <p className="source-line source-untranslated">
                  {t('sources.noEnglish')}<span className="source-licence"> · {text.licence}</span>
                </p>
              ) : (
                <p className="source-line">{t('sources.labelEnglish')}: {text.translation}<span className="source-licence"> · {text.licence}</span>{text.supplier && supplierName(text.supplier) && (<span className="source-licence"> · via {supplierName(text.supplier)}</span>)}</p>
              )}
              {text.secondTranslation && <p className="source-line">{t('sources.labelSecond')}: {text.secondTranslation}</p>}
              {text.thirdTranslation && <p className="source-line">{t('sources.labelThird')}: {text.thirdTranslation}</p>}
              {/* The fourth English and beyond, each with its OWN licence beside it. A text may
                  offer four renderings on four different terms — the Douay-Rheims is public
                  domain by age whatever the corpus around it is licensed under — and one licence
                  printed for the whole text would be a false statement about three of them. */}
              {/*
                * THE NOTES WERE DECLARED AND NEVER SHOWN — corrected 2026-08-16.
                *
                * This printed a name and a licence. Every rendering also declares a `note` saying
                * what it actually is, and none of them had ever reached a reader — the Septuagint's
                * least excusably, since it has said "translated from the Greek Septuagint, not from
                * the Masoretic Hebrew the other English translations here render" since the day it
                * was added. That sentence is the difference between a reader thinking two
                * translators disagreed and knowing two TEXTS do.
                *
                * Found by checking whether a test written an hour earlier — every rendering must
                * carry a note — was enforcing anything a reader could see. It was not.
                */}
              {text.extraTranslations?.map(extra => (
                <div key={extra.id}>
                  <p className="source-line">
                    {t('sources.labelAlso')}: {extra.name}
                    {/*
                      * HOW MUCH OF THE TEXT IT ACTUALLY COVERS — added 2026-08-17.
                      *
                      * The sentence below explains that renderings are withheld where the
                      * chapters divide differently, which is the CLASS. It cannot say the
                      * MAGNITUDE, and the magnitude is what separates one rendering from
                      * another: on the Hebrew Bible, Leeser is on every verse and the Metsudah
                      * on 37% of them. Told only the rule, a reader meets both as "sometimes
                      * missing" and has no way to know that one is nearly whole and the other
                      * is a third of a book.
                      *
                      * Measured by the build off the verses it wrote, never declared — see
                      * `renderingsWithCounts`. `unit.passages` rather than a new "verses" key,
                      * because the reports panel already counts in passages and one word for
                      * one idea is worth more than a shade of precision in three languages.
                      */}
                    {typeof extra.verses === 'number' && (
                      <span className="source-licence"> · {extra.verses.toLocaleString()} {t('unit.passages')}</span>
                    )}
                    {extra.licence && <span className="source-licence"> · {extra.licence}</span>}
                    {/*
                      * WHOSE COPY THIS IS, which is a different fact from what it is licensed under.
                      * 26 of this library’s licence declarations read “Public domain” - true of the text and
                      * silent about where it came from. The Douay-Rheims arrives through scrollmapper, the
                      * Vulgate through eBible, the Peshitta through CrossWire, and a reader asking which copy
                      * they were reading could not tell.
                      *
                      * Resolved to a name rather than printing the id: `scrollmapper` is a slug for maintenance,
                      * and `scrollmapper/bible_databases` is what a person can go and look up.
                      */}
                    {extra.supplier && supplierName(extra.supplier) && (
                      <span className="source-licence"> · via {supplierName(extra.supplier)}</span>
                    )}
                  </p>
                  {extra.note && <p className="source-line source-muted">{extra.note}</p>}
                </div>
              ))}
              {/*
                * WHY A RENDERING A READER SAW ON ONE VERSE IS MISSING ON ANOTHER — added 2026-08-16.
                *
                * Every rendering here is gated on the chapter dividing verses the same way, and
                * where it does not, the rendering is withheld rather than shifted. That is the
                * single most consequential decision in this library — the Douay-Rheims once
                * shipped a whole Psalter one verse-set out, and the Septuagint is carried on only
                * 17,586 of 23,145 verses because of it.
                *
                * All of that was written in comments. To a reader the effect was simply that the
                * Septuagint appears on Genesis 1 and not on Psalm 23, which reads as a patchy
                * import rather than as a refusal to show the wrong verse under a right-looking
                * number.
                *
                * ONE SENTENCE FOR ALL OF THEM, not a clause repeated in every note. This is a fact
                * about how the library works, not about any one text — the Vulgate's own note said
                * it yesterday and the eight older renderings did not, which is the same "fixed the
                * instance, read it as the class" mistake this file keeps meeting.
                *
                * AND THE SENTENCE HAD TO WIDEN WHEN THE CLASS DID — 2026-08-19. It read that a
                * rendering is shown "only where its chapter divides verses the same way", which was
                * true of every rendering carried when it was written: they were all partial for
                * that one reason. Three added since are partial for others — **the Syriac Peshitta
                * because its canon never held 2 Peter, 2 and 3 John, Jude or Revelation, and
                * Reckendorf's Hebrew Quran because 52 of its 114 surahs are transcribed with a
                * verse missing in the middle or a marker past the end.**
                *
                * A universal sentence told a reader, in three languages, that Reckendorf divides
                * verses differently. He does not. It now names chapter division as the usual reason
                * and points at the rendering's own note for the rest — which keeps the one-sentence
                * design and stops it claiming more than it knows.
                */}
              {text.extraTranslations && text.extraTranslations.length > 0 && (
                <p className="source-line source-muted">{t('sources.renderingGate')}</p>
              )}
              {text.partialCanon && (
                <p className="source-line source-note">
                  {t('sources.heldAsScripture')}: {text.partialCanon.heldBy}. {t('sources.notHeldAsScripture')}: {text.partialCanon.notHeldBy}.
                </p>
              )}
              {text.longerCanon && (
                <p className="source-line source-note">
                  {t('sources.alsoHeldBy')} {text.longerCanon.heldBy}: {text.longerCanon.beyond}.
                </p>
              )}
              {/* The third direction. Rendered next to the other two rather than folded into
                  either, because "holds more" and "holds fewer" are opposite facts about a reader
                  and the panel is where they find out which one is theirs. */}
              {text.shorterCanon && (
                <p className="source-line source-note">
                  {t('sources.notInCanonOf')} {text.shorterCanon.heldBy}: {text.shorterCanon.without}.
                </p>
              )}
              {text.translationNote && <p className="source-line source-note">{text.translationNote}</p>}
              {text.originalEdition && (
                <p className="source-line source-muted">{t('sources.labelOriginal')}: {text.originalEdition}{text.originalLanguage ? ` (${text.originalLanguage})` : ''}</p>
              )}
              {/* Directly under the edition it qualifies, because it is about THAT line — which
                  reading, and whether its verse numbers are the ones this library counts by. */}
              {text.originalNote && <p className="source-line source-note">{text.originalNote}</p>}
              {/*
                * GROUPED BY STANDING, not listed flat.
                *
                * This was one line naming every collection with its count, which was right while
                * the shelf was Bukhari and Muslim — two names, one standing, nothing to
                * distinguish. Six collections on one line say that all six weigh the same, and
                * the four Sunan are canonical without being ṣaḥīḥ throughout. Grouping keeps it
                * to two lines and puts the distinction where the names are, rather than adding a
                * caveat somewhere else that a reader has to connect for themselves.
                */}
              {groupByStanding(text.collections).map(([standing, cols]) => (
                <p className="source-line source-muted" key={standing || 'ungraded'}>
                  {cols.map(c => `${c.name} — ${c.passages.toLocaleString()}`).join(" · ")}
                  {standing ? ` — ${standing}` : ''}
                </p>
              ))}
              {text.digitisation && <p className="source-line source-muted">{text.digitisation}</p>}
            </li>
          ))}
        </ul>

        {heldBack.length > 0 && (
          <section className="sources-held">
            <h3 className="source-name">{t('sources.heldBack')}</h3>
            <p className="tellings-lead">{t('sources.heldBackLead')}</p>
            <ul className="sources-list">
              {heldBack.map(item => (
                <li key={item.edition} className="source-item">
                  <p className="source-line">{item.edition}<span className="source-licence"> · {item.licence}</span></p>
                  <p className="source-line source-muted">
                    {item.passages.toLocaleString()} {t('unit.passages')}{item.awaiting ? ` — ${t('sources.awaiting', { name: item.awaiting })}` : ''}
                  </p>
                  {item.corporaNames && item.corporaNames.length > 0 && (
                    <p className="source-line source-muted">
                      {t('sources.heldBackAffects')}: {item.corporaNames.join(', ')}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/*
          * WHAT ARRIVES ON A DATE — placed after the withheld and before the absent.
          *
          * The ordering is the argument. A reader has just been told some texts wait on somebody's
          * permission; they are about to be told others do not exist here at all. Between those two
          * sits the only category that resolves itself, and putting it anywhere else would leave the
          * page reading as though every gap were permanent.
          */}
        {expiries.length > 0 && (
          <section className="sources-expiries">
            <h3 className="source-name">{t('sources.expiries')}</h3>
            <p className="tellings-lead">{t('sources.expiriesLead')}</p>
            <ul className="sources-list">
              {expiries.map(item => {
                const months = yearsAway(new Date().toISOString().slice(0, 10), item.freeFrom);
                return (
                  <li key={item.text} className="source-item">
                    <p className="source-line">
                      {item.text}
                      <span className="source-licence"> · {t('sources.freeFrom', { date: item.freeFrom.slice(0, 4) })}</span>
                    </p>
                    <p className="source-line source-muted">
                      {months <= 18
                        ? t('sources.expiryMonths', { n: String(Math.max(months, 0)) })
                        : t('sources.expiryYears', { n: String(Math.round(months / 12)) })}
                      {' — '}{item.unblocks}
                    </p>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/*
          * The branches with nothing here at all — which is a different statement from the one
          * above it, and the ordering is deliberate. Held-back texts are ours and withheld;
          * these were never obtained. A reader who scrolled looking for their own tradition and
          * found only other people's should reach the reason without having to ask for it.
          */}
        {absent.length > 0 && (
          <section className="sources-absent">
            <h3 className="source-name">{t('sources.absent')}</h3>
            {/*
              * THIS LEAD SAID "These traditions have no text in this library" AND IT WAS FALSE FOR
              * HALF OF THEM — the same sentence, and the same fault, as the one on `texts.html`.
              *
              * The blocker vocabulary gives it away. `permission` means nobody has said yes and
              * nothing is here; `transcription` and `translation` both presuppose an original, and
              * every such row has text in hand: Alevism 2,252 lines of nefes, the Ahmadiyya Lahore
              * 4,488 verses of Muhammad Ali's Quran, the Beta Israel the Orit complete at eight of
              * eight. A reader of those branches was told this library holds nothing of theirs.
              *
              * Derived from the rows rather than written into the string, so it moves when the
              * registry does — and fixed on BOTH surfaces, which `CLAUDE.md` requires and which the
              * page-only fix would have broken.
              */}
            <p className="tellings-lead">{t('sources.absentLead')}</p>
            {absent.some(a => a.blockedBy !== 'permission') && (
              <p className="tellings-lead">
                {t('sources.absentSplit', {
                  total: String(absent.length),
                  awaited: String(absent.filter(a => a.blockedBy === 'permission').length),
                  partly: String(absent.filter(a => a.blockedBy !== 'permission').length),
                })}
              </p>
            )}
            <ul className="sources-list">
              {absent.map(item => (
                <li key={item.branch} className="source-item">
                  <p className="source-line">
                    {item.branch}
                    <span className="source-licence"> · {t(BLOCKER_LABEL[item.blockedBy])}</span>
                  </p>
                  <p className="source-line source-muted">{t('sources.absentHolds')}: {item.holds}</p>
                  <p className="source-line source-muted">{item.detail}</p>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/*
          * THE THIRD KIND OF MISSING, and the only one where nobody else is to blame.
          *
          * The held-back list says a publisher has not said yes. The absent list says a branch has
          * nothing here and why. Between them sat 33,125 passages that are fetched, paired with
          * their originals, and free to publish — waiting only on somebody deciding what to call
          * them. A reader got silence, and silence reads as "there is none".
          *
          * The sharpest case: the New Testament now tells Assyrian readers their canon is 22 books
          * rather than 27, which is true and useful, and it was printed while the Peshitta itself
          * sat unpublished with nothing saying so.
          */}
        {unshelved.length > 0 && (
          <section className="sources-unshelved">
            <h3 className="source-name">{t('sources.unshelved')}</h3>
            <p className="tellings-lead">{t('sources.unshelvedLead', { count: unshelvedTotal(unshelved).toLocaleString() })}</p>
            <ul className="sources-list">
              {unshelved.map(item => (
                <li key={item.name} className="source-item">
                  <p className="source-line">
                    {item.name}
                    <span className="source-licence"> · {item.passages.toLocaleString()} {t('sources.unshelvedPassages')}</span>
                  </p>
                  <p className="source-line source-muted">{item.licence}</p>
                  <p className="source-line source-muted">{item.detail}</p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </aside>
  );
}
